import { FieldError, useErrorId } from '@calendly/form/parts';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { SelectUI, ControlUI, OptionsUI } from '@calendly/ui/components/select';
import classNames from 'classnames';
import { useEffect, useRef, useState, forwardRef } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { SelectProvider } from './context';
const styledInputIsExpandedCss = "s1dtv3de";
const styledInputIsInvalidCss = "sph3lkg";
const styledInputCss = "s165pud0";
const StyledInput = forwardRef((props, ref) => {
  const {
    isExpanded,
    isInvalid,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'input';
  return <Component ref={ref} {...restProps} className={classNames(styledInputCss, {
    [styledInputIsExpandedCss]: isExpanded,
    [styledInputIsInvalidCss]: isInvalid
  }, props.className)} />;
});
const wrapperCss = "wmeth2s";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const overlayCss = "o1g7z9b3";
const Overlay = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(overlayCss, props.className)} />;
});
const optionsContainerCss = "o1d59tpr";
const OptionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionsContainerCss, props.className)} />;
});
const OverlayContent = ({
  isExpanded,
  selectedOption
}) => {
  if (isExpanded || !selectedOption) return null;
  return <Overlay data-testid="selected-option">{selectedOption}</Overlay>;
};
export const SearchableSelectField = ({
  name,
  label = null,
  displayValue = null,
  options = [],
  onChange,
  onSearch,
  placeholder = null,
  emptyPlaceholder = null,
  error = null,
  onFocus = null,
  defaultSearchQuery = '',
  onUpdate,
  children,
  ...props
}) => {
  const controlId = `${name}_controls`;
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const [searchQuery, setSearchQuery] = useState(defaultSearchQuery);
  const inputRef = useRef(null);
  const errorId = useErrorId({
    error
  });
  const debouncedQuery = useDebounce(searchQuery, 300);
  useEffect(() => {
    if (onSearch && isExpanded) onSearch(debouncedQuery);
  }, [onSearch, debouncedQuery, isExpanded]);
  useEffect(() => {
    if (!isExpanded) {
      if (onSearch && searchQuery !== defaultSearchQuery) onSearch(defaultSearchQuery);
      setSearchQuery(defaultSearchQuery);
    }
  }, [isExpanded, setSearchQuery, onSearch, searchQuery, defaultSearchQuery]);
  useEffect(() => {
    if (isExpanded) {
      inputRef.current.focus();
    } else {
      inputRef.current.blur();
    }
  }, [isExpanded, inputRef]);
  const onInputChange = e => {
    setSearchQuery(e.target.value);
  };
  const handleFocus = e => {
    toggleDropdown(e);
    if (onFocus) onFocus();
  };
  const getPlaceholder = () => {
    if (isExpanded) return placeholder;
    if (!displayValue) return emptyPlaceholder || placeholder;
    return null;
  };
  const invalid = Boolean(error);
  const onKeyDown = e => {
    if (e.code === 'Enter') {
      onUpdate(e);
      toggleDropdown(e);
    }
  };
  const onBlur = e => {
    onUpdate(e);
  };
  return <SelectProvider value={{
    options,
    onChange
  }}>
      <SelectUI>
        <ControlUI as="label" expanded={isExpanded} invalid={invalid}>
          <Wrapper aria-label={label} {...props} role="combobox" aria-expanded={isExpanded} aria-describedby={errorId} aria-controls={controlId} aria-invalid={Boolean(error)} name={name}>
            <StyledInput name={`${name}_search`} ref={inputRef} aria-label="Search" role="searchbox" onFocus={handleFocus} onBlur={onBlur} onChange={onInputChange} onKeyDown={onKeyDown} value={isExpanded ? searchQuery : ''} placeholder={getPlaceholder()} autoComplete="off" autoCorrect="off" isExpanded={isExpanded} isInvalid={Boolean(error)}
          // prevent LastPass extension from filling this input
          data-lpignore="true" />
            <OverlayContent isExpanded={isExpanded} selectedOption={displayValue} />
          </Wrapper>
        </ControlUI>
        <Dropdown>
          <OptionsContainer>
            <OptionsUI onClose={toggleDropdown} id={controlId} role="listbox" aria-label="Options" invalid={invalid}>
              {children}
            </OptionsUI>
          </OptionsContainer>
        </Dropdown>
      </SelectUI>
      <FieldError text={error} id={errorId} />
    </SelectProvider>;
};

require("./searchable-select-field.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./searchable-select-field.js");